import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { isSuperAdminEmail } from "@/config/platform";
import UnifiedTopBar from "@/components/UnifiedTopBar";
import {
  ExternalLink,
  Copy,
  Users,
  Shield,
  Sparkles,
  ChefHat,
  Heart,
  PartyPopper,
  Plane,
  ShoppingBag,
  ArrowRight,
  Scissors,
  Eye,
  Presentation,
  Cannabis,
  FileText,
  CreditCard,
  BarChart3,
  UserPlus,
  Camera,
  ChevronDown,
  ChevronRight,
  Monitor,
  Download,
  Globe,
  LayoutDashboard,
  Send,
  Star,
  Wallet,
  User,
  ClipboardList,
  CalendarDays,
  Link2,
  CheckCircle2,
  Settings,
  Grid3X3,
  Upload
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = API || window.location.origin;

const NailPolishIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M10 2h4v6l1 1v3a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V9l1-1V2z" />
    <path d="M10 14v6a2 2 0 0 0 2 2v0a2 2 0 0 0 2-2v-6" />
    <line x1="10" y1="5" x2="14" y2="5" />
  </svg>
);

// ───── LIVE EVENT IDS (dynamically seeded) ─────
const CATEGORY_MAP = {
  hair:        { icon: Scissors,       iconColor: "#e83e8c", bgColor: "#ffeef4", name: "Hair",        host: "Zuri Chen",      city: "Miami",       company: "Zuri Hair Lab",         token: "ml_zuri_hair_2026",          eventId: "evt_ab240b208f20" },
  makeup:      { icon: Sparkles,       iconColor: "#9c57f5", bgColor: "#f2e9ff", name: "Makeup",      host: "Aria James",     city: "Los Angeles", company: "Aria James Beauty",     token: "ml_aria_makeup_2026",        eventId: "evt_cf7afba1c6fd" },
  nails:       { icon: NailPolishIcon, iconColor: "#f15a70", bgColor: "#ffe8ec", name: "Nails",       host: "Tiana Brooks",   city: "London",      company: "Luxe Nails Studio",     token: "ml_tiana_nails_2026",        eventId: "evt_1a40ded39caf" },
  lashes:      { icon: Eye,            iconColor: "#774cda", bgColor: "#ede7f7", name: "Lashes",      host: "Diamond Wells",  city: "New Jersey",  company: "Diamond Lash Studio",   token: "ml_diamond_lashes_2026",     eventId: "evt_21e3928220b8" },
  events:      { icon: PartyPopper,    iconColor: "#f9c434", bgColor: "#fff5e6", name: "Events",      host: "Nadia Cole",     city: "Paris",       company: "Cole Events Co",        token: "ml_nadia_events_2026",       eventId: "evt_85bcaff9d17f" },
  instructors: { icon: Presentation,   iconColor: "#4a90d9", bgColor: "#e0eeff", name: "Instructors", host: "Aria James",     city: "Los Angeles", company: "Aria James MUA Academy",token: "ml_aria_instructors_2026",   eventId: "evt_dbb4162b6325" },
  culinary:    { icon: ChefHat,        iconColor: "#f28c28", bgColor: "#fff3e6", name: "Culinary",    host: "Marcus Rivera",  city: "Charlotte",   company: "Chef Marcus Kitchen",   token: "ml_marcus_culinary_2026",    eventId: "evt_766747e575b1" },
  wellness:    { icon: Heart,          iconColor: "#75d6d6", bgColor: "#e7f5f5", name: "Wellness",    host: "Simone Hart",    city: "New York",    company: "Hart Wellness Studio",  token: "ml_simone_wellness_2026",    eventId: "evt_712799620436" },
  travel:      { icon: Plane,          iconColor: "#73b7ff", bgColor: "#e7f3ff", name: "Travel",      host: "Jordan Blake",   city: "Barcelona",   company: "Blake Travel Co",       token: "ml_jordan_travel_2026",      eventId: "evt_8a5458cf6aa2" },
  dispensary:  { icon: Cannabis,       iconColor: "#60d680", bgColor: "#e8fef0", name: "Dispensary",  host: "Sage Rivera",    city: "Cape Town",   company: "Green Leaf Co",         token: "ml_sage_dispensary_2026",    eventId: "evt_c52b7e778b8a" },
  retail:      { icon: ShoppingBag,    iconColor: "#9950fa", bgColor: "#f0e8ff", name: "Retail",      host: "Camille Duval",  city: "Milan",       company: "Duval Style Shop",      token: "ml_camille_retail_2026",     eventId: "evt_bc362c151aaa" },
};

const CATEGORY_ORDER = ["hair","makeup","nails","lashes","events","instructors","culinary","wellness","travel","dispensary","style"];

// ───── COMPREHENSIVE FLOW DEFINITIONS ─────
// Each flow represents a real user journey with user type labels
const FLOW_CONFIG = [
  {
    id: "discovery",
    title: "1. Discovery & Sales Pages",
    subtitle: "Public visitor explores the platform",
    userType: "Anyone (no login required)",
    icon: Globe,
    color: "#6b7280",
    description: "A potential host or curious visitor lands on the Phygital homepage. They see the brand, browse category specific sales pages (Hair, Makeup, Culinary, etc.), and view pricing. Each sales page has tailored messaging, hero imagery, feature highlights, social proof, and a clear 'Get Started' CTA.",
    screenIds: ["coming_soon", "homepage", "pricing", "sales_hair", "sales_makeup", "sales_culinary", "sales_events", "sales_instructors", "sales_wellness", "sales_nails", "sales_lashes", "sales_travel", "sales_dispensary", "sales_retail"],
  },
  {
    id: "onboard",
    title: "2. Host Signup & Free Trial",
    subtitle: "Provider signs up and gets their dashboard",
    userType: "New host (converting from visitor)",
    icon: UserPlus,
    color: "#10b981",
    description: "A provider clicks 'Get Started' from any sales page. Step 1: pick business category from 11 options. Step 2: enter name, email, business info. Step 3: Stripe checkout with 60-day trial. Step 4: land on their new host dashboard.",
    screenIds: ["onboard_start", "onboard_form"],
  },
  {
    id: "flow_makeup",
    title: "3. Makeup - Full Experience (Aria James, LA)",
    subtitle: "Host POV + Client POV: flagship deep dive",
    userType: "Host: Aria James | Client: Jessica Williams",
    icon: Sparkles,
    color: "#9c57f5",
    description: "Aria James runs a makeup studio in LA. Her dashboard shows her client pipeline as a Kanban board with each card showing client name, booked service, appointment date/time, and inspo previews. She toggles to Calendar view, browses Preference Cards, manages Services with upsells linked to her Instructor dashboard, reviews Insights, and configures Settings. On the CLIENT side: Jessica Williams receives an animated category-colored invitation, RSVPs, and gets a confirmation page showing her preferences, linked experiences from other categories, service upsells, and host resources.",
    screenIds: [
      "host_makeup_board", "host_makeup_calendar", "host_makeup_clients",
      "host_makeup_pref_cards", "host_makeup_services", "host_makeup_insights",
      "host_makeup_settings", "host_makeup_switcher",
      "client_makeup_invite", "client_makeup_confirm", "client_makeup_prefs", "client_makeup_upsells",
    ],
  },
  {
    id: "flow_hair",
    title: "4. Hair - Full Experience (Zuri Chen, Miami)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Zuri Chen | Client: Aisha Johnson",
    icon: Scissors,
    color: "#e83e8c",
    description: "Zuri Chen's hair salon dashboard in Miami. Her Kanban shows clients with hair-specific services (Silk Press, Locs Maintenance, Color Treatment). Calendar view spreads appointments across the week. On the CLIENT side: Aisha Johnson receives a pink-themed invitation, RSVPs with her hair preferences (texture, length, protective styles), and sees her confirmation page with appointment details and linked experiences.",
    screenIds: ["host_hair_board", "host_hair_calendar", "client_hair_invite", "client_hair_confirm"],
  },
  {
    id: "flow_culinary",
    title: "5. Culinary - Full Experience (Marcus Rivera, Charlotte)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Marcus Rivera | Client: David Thompson",
    icon: ChefHat,
    color: "#f28c28",
    description: "Chef Marcus manages private dining in Charlotte. His dashboard highlights dietary preferences prominently. On the CLIENT side: David Thompson receives an orange-themed invitation for a Custom Celebration Cake tasting, RSVPs with dietary restrictions and allergies, and sees his confirmation page with full dietary profile and menu preferences.",
    screenIds: ["host_culinary_board", "host_culinary_calendar", "client_culinary_invite", "client_culinary_confirm"],
  },
  {
    id: "flow_nails",
    title: "6. Nails - Full Experience (Tiana Brooks, London)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Tiana Brooks | Client: Priya Desai",
    icon: NailPolishIcon,
    color: "#f15a70",
    description: "Tiana Brooks runs Luxe Nails Studio in London. Her dashboard shows nail-specific services. On the CLIENT side: Priya Desai receives a coral-pink invitation, RSVPs with nail shape, length, and design preferences, and sees her confirmation with style inspo images.",
    screenIds: ["host_nails_board", "host_nails_calendar", "client_nails_invite", "client_nails_confirm"],
  },
  {
    id: "flow_lashes",
    title: "7. Lashes - Full Experience (Diamond Wells, NJ)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Diamond Wells | Client: Keisha Ford",
    icon: Eye,
    color: "#774cda",
    description: "Diamond Wells runs Diamond Lash Studio in New Jersey. Her dashboard manages lash extension appointments. On the CLIENT side: Keisha Ford receives a violet-themed invitation, RSVPs with lash curl, length, and material preferences, and sees her confirmation page.",
    screenIds: ["host_lashes_board", "host_lashes_calendar", "client_lashes_invite", "client_lashes_confirm"],
  },
  {
    id: "flow_events",
    title: "8. Events - Full Experience (Nadia Cole, Paris)",
    subtitle: "Host POV + Client POV (event language)",
    userType: "Host: Nadia Cole | Client: Claire & James Mitchell",
    icon: PartyPopper,
    color: "#f9c434",
    description: "Nadia Cole runs Cole Events Co in Paris. Her dashboard uses event vocabulary: 'Participant' instead of 'Client', 'Check-in' instead of 'Appointment'. On the CLIENT side: Claire & James Mitchell receive a gold-themed invitation for their event, RSVP with accessibility and dietary needs, and see their confirmation page with event check-in details.",
    screenIds: ["host_events_board", "host_events_calendar", "client_events_invite", "client_events_confirm"],
  },
  {
    id: "flow_instructors",
    title: "9. Instructors - Full Experience (Aria James, LA)",
    subtitle: "Host POV + Client POV (class language)",
    userType: "Host: Aria James (Academy) | Client: Mia Rodriguez",
    icon: Presentation,
    color: "#4a90d9",
    description: "Aria's second dashboard: Aria James MUA Academy. This instructor dashboard manages class enrollments with class-specific language. On the CLIENT side: Mia Rodriguez receives a blue-themed invitation for a makeup masterclass, RSVPs, and sees her confirmation page with class schedule and materials.",
    screenIds: ["host_instructors_board", "host_instructors_calendar", "client_instructors_invite", "client_instructors_confirm"],
  },
  {
    id: "flow_wellness",
    title: "10. Wellness - Full Experience (Simone Hart, NYC)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Simone Hart | Client: Elena Kowalski",
    icon: Heart,
    color: "#75d6d6",
    description: "Simone Hart runs Hart Wellness Studio in New York. Her dashboard manages yoga, meditation, massage, and holistic wellness appointments. On the CLIENT side: Elena Kowalski receives a teal-themed invitation, RSVPs with wellness goals and pressure preferences, and sees her confirmation page.",
    screenIds: ["host_wellness_board", "host_wellness_calendar", "client_wellness_invite", "client_wellness_confirm"],
  },
  {
    id: "flow_travel",
    title: "11. Travel - Full Experience (Jordan Blake, Barcelona)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Jordan Blake | Client: The Nakamura Couple",
    icon: Plane,
    color: "#73b7ff",
    description: "Jordan Blake runs Blake Travel Co in Barcelona. Dashboard manages travel experience bookings. On the CLIENT side: The Nakamura Couple receive a blue-themed invitation for a curated travel experience, RSVP with travel preferences and accessibility needs, and see their confirmation page.",
    screenIds: ["host_travel_board", "host_travel_calendar", "client_travel_invite", "client_travel_confirm"],
  },
  {
    id: "flow_dispensary",
    title: "12. Dispensary - Full Experience (Sage Rivera, Cape Town)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Sage Rivera | Client: Cameron Lee",
    icon: Cannabis,
    color: "#60d680",
    description: "Sage Rivera runs Green Leaf Co in Cape Town. Dashboard manages dispensary experience bookings. On the CLIENT side: Cameron Lee receives a green-themed invitation, RSVPs with consumption preferences, and sees their confirmation page.",
    screenIds: ["host_dispensary_board", "host_dispensary_calendar", "client_dispensary_invite", "client_dispensary_confirm"],
  },
  {
    id: "flow_retail",
    title: "13. Retail - Full Experience (Camille Duval, Milan)",
    subtitle: "Host POV + Client POV",
    userType: "Host: Camille Duval | Client: Isabella Rossi",
    icon: ShoppingBag,
    color: "#9950fa",
    description: "Camille Duval runs Duval Style Shop in Milan. Dashboard manages personal styling appointments and retail experiences. On the CLIENT side: Isabella Rossi receives a purple-themed invitation, RSVPs with style and size preferences, and sees her confirmation page.",
    screenIds: ["host_retail_board", "host_retail_calendar", "client_retail_invite", "client_retail_confirm"],
  },
  {
    id: "consumer",
    title: "14. Consumer Profile & Linked Experiences (/me)",
    subtitle: "Client's preference wallet, experience planner, and share cards",
    userType: "Authenticated user (any role)",
    icon: User,
    color: "#8b5cf6",
    description: "Every user has a profile at /me. Home tab shows their PHY card and upcoming events. Defaults tab has 10 preference categories filled once and reused across all bookings. Inspo tab stores inspiration images. Experiences tab has three views: Explore (browse categories), Calendar (upcoming appointments and linked experiences on a timeline), and Plan (wizard to plan a personal event like a Wedding that auto-creates linked experiences across Hair, Makeup, Nails, and Lashes). The Share Card is the viral loop: clients share preference cards with external providers via public links showing their full preferences, inspo, and linked event context.",
    screenIds: [
      "me_home", "me_defaults", "me_defaults_hair", "me_defaults_dietary",
      "me_inspo", "me_experiences_explore", "me_experiences_calendar", "me_experiences_plan",
      "me_settings", "share_card",
    ],
  },
  {
    id: "admin",
    title: "15. Admin Dashboard",
    subtitle: "Full platform control (4 tabs, 8 product sidebar items)",
    userType: "Super Admin (Naomi Thomas)",
    icon: Shield,
    color: "#1e293b",
    description: "4 top-level tabs: DASHBOARD (live metrics, pending messages, recent activity), PEOPLE (Users grid, Orgs with category badges, all 11 Host Dashboards), PRODUCT (Launch Control, Defaults, Integrations, Core Files, Messages), FINANCIALS (MRR, subscription breakdowns, revenue projections).",
    screenIds: [
      "admin_dashboard", "admin_analytics",
      "admin_users", "admin_orgs", "admin_host_dashboards",
      "admin_launch", "admin_defaults", "admin_integrations", "admin_core_files",
      "admin_financials",
    ],
  },
  {
    id: "portals",
    title: "16. Investor & Advocate Portals",
    subtitle: "Stakeholder dashboards with real time metrics",
    userType: "Investor / Advocate (portal access required)",
    icon: BarChart3,
    color: "#6366f1",
    description: "The Investor Portal shows financial KPIs, growth metrics, and an interactive pitch deck. Advocates get referral tracking, earnings, and a unique referral link.",
    screenIds: ["investor_landing", "advocate_dashboard"],
  },
];

// ───── SCREEN DESCRIPTIONS ─────
const SCREEN_DESCRIPTIONS = {
  // ─── Discovery ───
  coming_soon: { title: "Coming Soon / Landing", desc: "The public landing page at phygital.systems. Brand logo, waitlist email signup form, and social links. First thing any unauthenticated visitor sees." },
  homepage: { title: "Homepage (Authenticated)", desc: "Full homepage with rotating category headline ('Your [makeup/hair/culinary] preferences, perfectly communicated'), Store and Share vs I Provide Services CTAs, animated preference card stack, and Demos dropdown." },
  pricing: { title: "Pricing Page", desc: "Transparent pricing showing Free tier and Solo tier ($11/mo). Team and Luxe tiers hidden until launch." },
  sales_hair: { title: "Hair Sales Page", desc: "Sales page for hair stylists. Captures hair texture, length, protective styles, and appointment vibe." },
  sales_makeup: { title: "Makeup Sales Page", desc: "Sales page for makeup artists. Skin type/tone intake, bridal and editorial portfolios, inspo sharing." },
  sales_culinary: { title: "Culinary Sales Page", desc: "Sales page for private chefs. Dietary restrictions, allergy management, menu customization." },
  sales_events: { title: "Events Sales Page", desc: "Sales page for event planners. RSVP management, check-in kiosks, Tap In music requests." },
  sales_instructors: { title: "Instructor Sales Page", desc: "Sales page for workshop leaders. Class enrollments, post-check-in resources, linked provider dashboards." },
  sales_wellness: { title: "Wellness Sales Page", desc: "Sales page for wellness providers, spa owners, and fitness instructors." },
  sales_nails: { title: "Nails Sales Page", desc: "Sales page for nail technicians. Nail shape, length, and design preference capture." },
  sales_lashes: { title: "Lashes Sales Page", desc: "Sales page for lash technicians. Curl, length, and style preference intake." },
  sales_travel: { title: "Travel Sales Page", desc: "Sales page for travel curators and tour operators." },
  sales_dispensary: { title: "Dispensary Sales Page", desc: "Sales page for dispensary experience providers." },
  sales_retail: { title: "Retail Sales Page", desc: "Sales page for retail and personal styling services." },

  // ─── Onboard ───
  onboard_start: { title: "Get Started: Category Picker", desc: "Step 1 of host signup. Provider selects their business category from 11 options." },
  onboard_form: { title: "Get Started: Business Info Form", desc: "Step 2. Provider enters name, email, business info with category-specific questions." },

  // ─── Makeup Host POV ───
  host_makeup_board: { title: "Host: Kanban Board", desc: "Aria's client pipeline. Four columns: Invited, Preferences Shared, Confirmed, Completed. Each card shows client name, booked service, date/time, and inspo previews." },
  host_makeup_calendar: { title: "Host: Calendar View", desc: "Weekly calendar grid with appointment cards. Toggle Month/Week/Day views." },
  host_makeup_clients: { title: "Host: Clients Tab", desc: "Searchable client list with status badges, contact info, and preference detail modals." },
  host_makeup_pref_cards: { title: "Host: Preference Cards", desc: "Grid of client preference cards with thumbnails, service names, linked experiences, and preference summaries." },
  host_makeup_services: { title: "Host: Services Tab", desc: "Service menu with Bridal Glam, Editorial, Natural Everyday. Portfolio images and upsells including linked Instructor class." },
  host_makeup_insights: { title: "Host: Insights Tab", desc: "Analytics: booking trends, client demographics, popular services, revenue stats. Gated behind paid tier." },
  host_makeup_settings: { title: "Host: Settings Tab", desc: "Event settings, intake configuration, welcome message, display controls, Tap In toggle, sharing options." },
  host_makeup_switcher: { title: "Host: Dashboard Switcher", desc: "Aria switches between her Makeup dashboard and Instructor dashboard (MUA Academy) without logging out." },

  // ─── Makeup Client POV ───
  client_makeup_invite: { title: "Client: Invitation Page", desc: "Jessica receives a purple-themed animated envelope invitation from Aria James Beauty. After animation, shows event details, RSVP form with preference intake." },
  client_makeup_confirm: { title: "Client: Confirmation (Top)", desc: "Jessica's confirmation for Bridal Glam. Shows Pre-Event badge, host photo, appointment details, and inline preference display with Edit button." },
  client_makeup_prefs: { title: "Client: Preferences & Upsells", desc: "Scrolled: preferences with color-coded badges, '1 on 1 Makeup Class' upsell linked to Instructor dashboard, host resource links organized by phase." },
  client_makeup_upsells: { title: "Client: Full View", desc: "Further scrolled: check-in/cancel buttons, VIP list, Tap In song request, linked experiences and inspo images." },

  // ─── Hair ───
  host_hair_board: { title: "Host: Kanban Board", desc: "Zuri's pipeline with Silk Press, Locs Maintenance, Color Treatment services across appointment stages." },
  host_hair_calendar: { title: "Host: Calendar View", desc: "Zuri's weekly calendar with hair appointments spread across days and times." },
  client_hair_invite: { title: "Client: Invitation Page", desc: "Aisha receives a pink-themed animated invitation from Zuri Hair Lab. Event details and hair preference RSVP form." },
  client_hair_confirm: { title: "Client: Confirmation", desc: "Aisha's confirmation for Silk Press & Style. Hair preferences, appointment time, style inspo, and linked wedding experiences." },

  // ─── Culinary ───
  host_culinary_board: { title: "Host: Kanban Board", desc: "Marcus's culinary pipeline highlighting dietary needs. Private Chef Dinner, Custom Celebration Cake, Tasting Menu services." },
  host_culinary_calendar: { title: "Host: Calendar View", desc: "Marcus's weekly schedule with dinner and tasting events across evenings." },
  client_culinary_invite: { title: "Client: Invitation Page", desc: "David receives an orange-themed invitation from Chef Marcus Kitchen. Dietary preference RSVP form." },
  client_culinary_confirm: { title: "Client: Confirmation", desc: "David's confirmation for Custom Celebration Cake. Dietary restrictions, allergies, menu preferences prominently displayed." },

  // ─── Nails ───
  host_nails_board: { title: "Host: Kanban Board", desc: "Tiana's nail client pipeline with shape, length, and design preferences visible on cards." },
  host_nails_calendar: { title: "Host: Calendar View", desc: "Tiana's weekly calendar with nail appointments." },
  client_nails_invite: { title: "Client: Invitation Page", desc: "Priya receives a coral-pink invitation from Luxe Nails Studio. Nail preference RSVP form." },
  client_nails_confirm: { title: "Client: Confirmation", desc: "Priya's confirmation showing nail shape, length, design preferences, and French tip inspo images." },

  // ─── Lashes ───
  host_lashes_board: { title: "Host: Kanban Board", desc: "Diamond's lash extension pipeline with curl type and style preferences on cards." },
  host_lashes_calendar: { title: "Host: Calendar View", desc: "Diamond's weekly calendar with lash appointments." },
  client_lashes_invite: { title: "Client: Invitation Page", desc: "Keisha receives a violet-themed invitation from Diamond Lash Studio. Lash preference RSVP form." },
  client_lashes_confirm: { title: "Client: Confirmation", desc: "Keisha's confirmation showing lash curl, length, material preferences." },

  // ─── Events ───
  host_events_board: { title: "Host: Event Dashboard", desc: "Nadia's event dashboard using event vocabulary: Participant, Check-in, Location. Columns: Invited, Definitely Going, Checked In, Completed." },
  host_events_calendar: { title: "Host: Calendar View", desc: "Nadia's event calendar with participant check-in slots." },
  client_events_invite: { title: "Client: Invitation Page", desc: "Claire & James receive a gold-themed invitation from Cole Events Co. Event RSVP with accessibility and dietary needs." },
  client_events_confirm: { title: "Client: Confirmation", desc: "Claire & James's event confirmation with check-in details, accessibility accommodations, and event schedule." },

  // ─── Instructors ───
  host_instructors_board: { title: "Host: Class Dashboard", desc: "Aria's MUA Academy dashboard managing class enrollments with instructor-specific language." },
  host_instructors_calendar: { title: "Host: Calendar View", desc: "Academy class schedule with student enrollment counts per session." },
  client_instructors_invite: { title: "Client: Invitation Page", desc: "Mia receives a blue-themed invitation for a makeup masterclass at Aria James MUA Academy." },
  client_instructors_confirm: { title: "Client: Confirmation", desc: "Mia's class confirmation with schedule, materials list, and instructor info." },

  // ─── Wellness ───
  host_wellness_board: { title: "Host: Kanban Board", desc: "Simone's wellness pipeline: yoga, massage, meditation bookings with pressure and goal preferences." },
  host_wellness_calendar: { title: "Host: Calendar View", desc: "Simone's weekly wellness schedule." },
  client_wellness_invite: { title: "Client: Invitation Page", desc: "Elena receives a teal-themed invitation from Hart Wellness Studio. Wellness goal and pressure preference RSVP." },
  client_wellness_confirm: { title: "Client: Confirmation", desc: "Elena's confirmation with wellness preferences, pressure level, and session details." },

  // ─── Travel ───
  host_travel_board: { title: "Host: Kanban Board", desc: "Jordan's travel experience pipeline with accommodation and adventure preferences." },
  host_travel_calendar: { title: "Host: Calendar View", desc: "Jordan's travel booking calendar." },
  client_travel_invite: { title: "Client: Invitation Page", desc: "The Nakamura Couple receive a blue-themed invitation from Blake Travel Co for a curated experience." },
  client_travel_confirm: { title: "Client: Confirmation", desc: "The Nakamura Couple's travel confirmation with preferences, accessibility needs, and itinerary preview." },

  // ─── Dispensary ───
  host_dispensary_board: { title: "Host: Kanban Board", desc: "Sage's dispensary experience pipeline with consumption preferences." },
  host_dispensary_calendar: { title: "Host: Calendar View", desc: "Sage's booking calendar." },
  client_dispensary_invite: { title: "Client: Invitation Page", desc: "Cameron receives a green-themed invitation from Green Leaf Co." },
  client_dispensary_confirm: { title: "Client: Confirmation", desc: "Cameron's confirmation with consumption preferences and session details." },

  // ─── Retail ───
  host_retail_board: { title: "Host: Kanban Board", desc: "Camille's personal styling pipeline with size, style, and budget preferences." },
  host_retail_calendar: { title: "Host: Calendar View", desc: "Camille's styling appointment calendar." },
  client_retail_invite: { title: "Client: Invitation Page", desc: "Isabella receives a purple-themed invitation from Duval Style Shop." },
  client_retail_confirm: { title: "Client: Confirmation", desc: "Isabella's confirmation with style preferences, size info, and budget range." },

  // ─── Consumer /me ───
  me_home: { title: "/me Home", desc: "PHY card with name and photo, upcoming events, hosting links, and portal access buttons." },
  me_defaults: { title: "Defaults Tab", desc: "10 preference categories in a grid. Each card expands to show category-specific preference fields." },
  me_defaults_hair: { title: "Hair Defaults Editor", desc: "Hair texture (8 types), length, state, protective styles, color, concerns, appointment vibe." },
  me_defaults_dietary: { title: "Dietary Defaults Editor", desc: "8 diet types, 8 allergy categories, meat preferences, spice tolerance, notes." },
  me_inspo: { title: "Inspo Tab", desc: "Inspiration images organized by category folders. Reusable across all future bookings." },
  me_experiences_explore: { title: "Experiences: Explore", desc: "Browse experience categories with stats showing engagements." },
  me_experiences_calendar: { title: "Experiences: Calendar", desc: "Personal calendar showing upcoming appointments and linked experiences. Week/Month/Day toggle." },
  me_experiences_plan: { title: "Experiences: Plan", desc: "4-step wizard: choose event type (Wedding auto-creates Hair + Makeup + Nails + Lashes), pick dates, select services, review and save." },
  me_settings: { title: "Settings Tab", desc: "Profile info, linked accounts, portal access buttons." },
  share_card: { title: "Share Preference Card", desc: "Public page at /share/card/{id}. External providers see the client's full preferences, inspo images, and linked event context. CTA to join Phygital. This is the viral acquisition loop." },

  // ─── Admin ───
  admin_dashboard: { title: "Dashboard: Overview", desc: "Live platform metrics, quick actions, pending messages, recent activity." },
  admin_analytics: { title: "Dashboard: Analytics", desc: "Conversion funnels, pilot breakdowns by category, referral stats, growth trends." },
  admin_users: { title: "People: Users", desc: "Airtable-style user grid with search, sort, bulk actions, Beehiiv sync." },
  admin_orgs: { title: "People: Organizations", desc: "Org cards with category badges and nested member lists." },
  admin_host_dashboards: { title: "People: Host Dashboards", desc: "All 11 pilot host events by category with RSVP counts and direct dashboard links." },
  admin_launch: { title: "Product: Launch Control", desc: "Category status toggles: Live, Pilot, Coming Soon, Hidden." },
  admin_defaults: { title: "Product: Defaults", desc: "10 preference category configurations with inline field editing." },
  admin_integrations: { title: "Product: Integrations", desc: "7 platform integrations: Tap In, Acuity, Beehiiv, Cloudinary, Stripe, MongoDB, Emergent." },
  admin_core_files: { title: "Product: Core Files", desc: "Core docs with viewing, editing, and download buttons." },
  admin_financials: { title: "Financials: Overview", desc: "MRR dashboard, subscription breakdowns, revenue projections." },

  // ─── Portals ───
  investor_landing: { title: "Investor Portal", desc: "Financial KPIs, growth metrics, pitch deck, and company updates." },
  advocate_dashboard: { title: "Advocate Portal", desc: "Referral count, earnings tracker, commission simulator, unique referral link." },
};

// Documents for download
const DOWNLOAD_DOCS = [
  { id: "master_plan", label: "Master Plan", icon: FileText },
  { id: "prd", label: "PRD", icon: FileText },
  { id: "roadmap", label: "Roadmap", icon: FileText },
  { id: "changelog", label: "Changelog", icon: FileText },
  { id: "current_state", label: "Current State", icon: FileText },
  { id: "beehiiv", label: "Beehiiv", icon: FileText },
];

export default function AppTourPage() {
  const [activeTab, setActiveTab] = useState("flows");
  const [tourData, setTourData] = useState(null);
  const [expandedFlow, setExpandedFlow] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [screenshotList, setScreenshotList] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [editingScreen, setEditingScreen] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const isSuperAdmin = user && (
    user.role === "super_admin" ||
    user.is_super_admin === true ||
    (user.email && isSuperAdminEmail(user.email)) ||
    (user.roles || []).includes("admin") ||
    (user.roles || []).includes("super_admin")
  );

  const hasAccess = user && (
    user.role === "super_admin" ||
    user.role === "admin" ||
    user.is_super_admin === true ||
    (user.email && isSuperAdminEmail(user.email)) ||
    (user.roles || []).some(r => ["investor", "team", "network", "advocate"].includes(r)) ||
    user.investor_portal_id ||
    user.team_portal_id ||
    user.network_portal_id
  );

  useEffect(() => {
    if (!user) return;
    if (!hasAccess) return;
    const token = localStorage.getItem("session_token");
    // Fetch raw screenshot data directly
    fetch(`${API}/api/apptour/data`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTourData(d))
      .catch(() => {});
    // Also fetch raw screenshot map for accurate matching
    fetch(`${API}/api/apptour/screenshots-list`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.screenshots) setScreenshotList(d.screenshots); })
      .catch(() => {});
    // Fetch overrides
    fetch(`${API}/api/apptour/overrides`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.overrides) setOverrides(d.overrides); })
      .catch(() => {});
  }, [user, hasAccess]);

  const startEdit = (screenId, title, desc) => {
    setEditingScreen(screenId);
    setEditTitle(title);
    setEditDesc(desc);
  };

  const saveEdit = async (screenId) => {
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/apptour/override/${screenId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ title: editTitle, desc: editDesc }),
      });
      if (res.ok) {
        setOverrides(prev => ({ ...prev, [screenId]: { ...prev[screenId], title: editTitle, desc: editDesc } }));
        toast.success("Saved!");
      }
    } catch (e) {
      toast.error("Failed to save");
    }
    setEditingScreen(null);
  };

  const uploadScreenshot = async (screenId, file) => {
    if (!file) return;
    setUploading(screenId);
    const token = localStorage.getItem("session_token");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/api/apptour/upload-screenshot/${screenId}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        // Update screenshot map
        setScreenshotList(prev => {
          const existing = prev.find(s => s.screen_id === screenId);
          if (existing) {
            return prev.map(s => s.screen_id === screenId ? { ...s, screenshot_url: data.screenshot_url } : s);
          }
          return [...prev, { screen_id: screenId, screenshot_url: data.screenshot_url }];
        });
        toast.success("Screenshot uploaded!");
      }
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(null);
  };

  // Get screen data with overrides applied
  const getScreenData = (id) => {
    const base = SCREEN_DESCRIPTIONS[id] || { title: id, desc: "" };
    const override = overrides[id] || {};
    return {
      id,
      title: override.title || base.title,
      desc: override.desc || base.desc,
      screenshot_url: screenshotMap[id]?.screenshot_url,
    };
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-gray-600">Please sign in to access the App Tour.</p>
          <Link to="/login" className="inline-block px-6 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800" data-testid="apptour-login-link">Sign In</Link>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <UnifiedTopBar variant="user" currentUser={user} />
        <div className="flex items-center justify-center" style={{ minHeight: "calc(100vh - 56px)" }}>
          <div className="text-center space-y-4">
            <p className="text-gray-600">You don't have access to the App Tour.</p>
            <Link to="/me" className="inline-block px-6 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800" data-testid="back-to-profile">Back to Profile</Link>
          </div>
        </div>
      </div>
    );
  }

  // Build screenshot map from both sources
  const screenshotMap = {};
  (tourData?.personas || []).forEach(p => {
    (p.screens || []).forEach(s => {
      if (s.screenshot_url) screenshotMap[s.id] = s;
    });
  });
  // Override with direct screenshot list (more accurate)
  (screenshotList || []).forEach(s => {
    if (s.screenshot_url) screenshotMap[s.screen_id] = { screenshot_url: s.screenshot_url };
  });

  const allScreenIds = FLOW_CONFIG.flatMap(f => f.screenIds);
  const totalScreens = allScreenIds.length;
  const capturedScreens = allScreenIds.filter(id => screenshotMap[id]?.screenshot_url).length;

  const getDownloadUrl = (fileId) => {
    const token = localStorage.getItem("session_token");
    return `${API}/api/admin/core-files/${fileId}/download${token ? `?token=${token}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <UnifiedTopBar variant="user" currentUser={user} />

      {/* Header */}
      <div className="border-b border-gray-100 px-6 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900" data-testid="apptour-title">Phygital App Tour</h1>
              <p className="text-sm text-gray-400 mt-0.5">{totalScreens} screens across {FLOW_CONFIG.length} user journeys / {capturedScreens} captured</p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {isSuperAdmin && (
                <button onClick={() => setEditMode(!editMode)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${editMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="edit-mode-toggle">
                  <Settings className="w-3 h-3" />{editMode ? 'Exit Edit Mode' : 'Edit Mode'}
                </button>
              )}
              {DOWNLOAD_DOCS.map(doc => (
                <a key={doc.id} href={getDownloadUrl(doc.id)} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100 transition-colors"
                  data-testid={`download-${doc.id}`}><Download className="w-3 h-3" />{doc.label}</a>
              ))}
            </div>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
            {[
              { id: "flows", label: "User Journeys" },
              { id: "pilots", label: "11 Pilots" },
              { id: "screens", label: "All Screenshots" },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                data-testid={`tour-tab-${tab.id}`}>{tab.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* ─── USER JOURNEYS TAB ─── */}
        {activeTab === "flows" && (
          <div data-testid="flows-section">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">User Journeys</h2>
              <p className="text-xs text-gray-400">Every user type and their complete flow through the platform. Click to expand and see each step with screenshots.</p>
            </div>
            <div className="space-y-3">
              {FLOW_CONFIG.map((flow) => {
                const isOpen = expandedFlow === flow.id;
                const FlowIcon = flow.icon;
                const screens = flow.screenIds.map(id => getScreenData(id));
                const captured = screens.filter(s => s.screenshot_url).length;

                return (
                  <div key={flow.id} className="border rounded-xl overflow-hidden" data-testid={`flow-${flow.id}`}>
                    <button onClick={() => setExpandedFlow(isOpen ? null : flow.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: flow.color + '15' }}>
                          <FlowIcon className="w-4 h-4" style={{ color: flow.color }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{flow.title}</p>
                          <p className="text-xs text-gray-400">{flow.subtitle}</p>
                          <p className="text-[10px] text-gray-300 mt-0.5">User: {flow.userType}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">{captured}/{screens.length}</span>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t bg-gray-50/50 p-4">
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{flow.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {screens.map((screen, idx) => (
                            <div key={screen.id} className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow" data-testid={`screen-${screen.id}`}>
                              <div className="relative">
                                {screen.screenshot_url ? (
                                  <div className="aspect-[9/16] bg-gray-100 relative cursor-pointer group overflow-hidden rounded-lg border border-gray-200"
                                    onClick={() => !editMode && setLightboxImg(`${API}${screen.screenshot_url}`)}>
                                    <img src={`${API}${screen.screenshot_url}?t=${Date.now()}`} alt={screen.title} className="w-full h-full object-contain bg-white" loading="lazy" />
                                    {!editMode && (
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                                      </div>
                                    )}
                                    {editMode && (
                                      <label className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer hover:bg-black/40 transition-colors">
                                        <div className="text-center text-white">
                                          <Upload className="w-6 h-6 mx-auto mb-1" />
                                          <span className="text-[10px] font-medium">{uploading === screen.id ? 'Uploading...' : 'Replace Image'}</span>
                                        </div>
                                        <input type="file" accept="image/*" className="hidden"
                                          onChange={(e) => uploadScreenshot(screen.id, e.target.files[0])} />
                                      </label>
                                    )}
                                  </div>
                                ) : (
                                  <div className="aspect-[9/16] bg-gray-100 flex items-center justify-center relative">
                                    {editMode ? (
                                      <label className="absolute inset-0 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors">
                                        <div className="text-center text-gray-400">
                                          <Upload className="w-6 h-6 mx-auto mb-1" />
                                          <span className="text-[10px] font-medium">Upload Screenshot</span>
                                        </div>
                                        <input type="file" accept="image/*" className="hidden"
                                          onChange={(e) => uploadScreenshot(screen.id, e.target.files[0])} />
                                      </label>
                                    ) : (
                                      <p className="text-xs text-gray-400">Not yet captured</p>
                                    )}
                                  </div>
                                )}
                                <div className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow" style={{ backgroundColor: flow.color }}>
                                  {idx + 1}
                                </div>
                              </div>
                              <div className="p-3">
                                {editMode && editingScreen === screen.id ? (
                                  <div className="space-y-2">
                                    <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                                      className="w-full text-sm font-medium border rounded px-2 py-1" data-testid={`edit-title-${screen.id}`} />
                                    <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                                      className="w-full text-xs border rounded px-2 py-1 resize-none" rows={3} data-testid={`edit-desc-${screen.id}`} />
                                    <div className="flex gap-1">
                                      <button onClick={() => saveEdit(screen.id)} className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded font-medium" data-testid={`save-edit-${screen.id}`}>Save</button>
                                      <button onClick={() => setEditingScreen(null)} className="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] rounded font-medium">Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div onClick={() => editMode && startEdit(screen.id, screen.title, screen.desc)}
                                    className={editMode ? "cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition-colors" : ""}>
                                    <p className="text-sm font-medium text-gray-900">{screen.title}</p>
                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{screen.desc}</p>
                                    {editMode && <p className="text-[9px] text-blue-500 mt-1 font-medium">Click to edit</p>}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── PILOTS TAB ─── */}
        {activeTab === "pilots" && (
          <div data-testid="pilots-section">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">11 Pilot Dashboards</h2>
              <p className="text-xs text-gray-400">Each pilot host is mapped to a city with real demo data, services, clients, and magic link access. Click any button to enter their dashboard.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {CATEGORY_ORDER.map(key => {
                const cat = CATEGORY_MAP[key];
                const Icon = cat.icon;
                return (
                  <div key={key} className="rounded-xl border border-gray-100 bg-white p-4 hover:shadow-md transition-shadow" data-testid={`pilot-card-${key}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: cat.bgColor }}>
                        <Icon className="w-4 h-4" style={{ color: cat.iconColor }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{cat.name}</p>
                        <p className="text-xs text-gray-500">{cat.host} / {cat.city}</p>
                        <p className="text-[10px] text-gray-400">{cat.company}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <a href={`${BASE_URL}/auth/magic?token=${cat.token}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 text-center py-1.5 rounded-lg text-[11px] font-medium transition-colors"
                        style={{ backgroundColor: cat.bgColor, color: cat.iconColor }}>Login as Host</a>
                      <a href={`${BASE_URL}/host/v2/${cat.eventId}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 text-center py-1.5 rounded-lg text-[11px] font-medium bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors">Dashboard</a>
                      <button onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/auth/magic?token=${cat.token}`); toast.success("Link copied!"); }}
                        className="px-2 py-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 rounded-xl border-2 p-4" style={{ borderColor: "#f9c43440", backgroundColor: "#fff5e605" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#fff5e6" }}>
                  <Shield className="w-4 h-4" style={{ color: "#f9c434" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#b8860b" }}>Super Admin: Naomi Thomas</p>
                  <p className="text-xs text-gray-400">me@iamnaomithomas.com / Full platform access</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a href={`${BASE_URL}/auth/magic?token=ml_pilot_naomi_hthf_2026_bootstrap`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: "#fff5e6", color: "#b8860b" }}>Login as Admin</a>
                <a href={`${BASE_URL}/admin`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-500 text-xs font-medium hover:bg-gray-100">Admin Dashboard</a>
                <a href={`${BASE_URL}/me`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-500 text-xs font-medium hover:bg-gray-100">/me Profile</a>
              </div>
            </div>
          </div>
        )}

        {/* ─── ALL SCREENSHOTS TAB ─── */}
        {activeTab === "screens" && (
          <div data-testid="screens-section">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">All Screenshots</h2>
              <p className="text-xs text-gray-400">Visual grid of all {totalScreens} screens grouped by user journey. Click any to zoom.</p>
            </div>
            {FLOW_CONFIG.map((flow) => {
              const FlowIcon = flow.icon;
              const screens = flow.screenIds.map(id => getScreenData(id));
              return (
                <div key={flow.id} className="mb-6" data-testid={`grid-${flow.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <FlowIcon className="w-4 h-4" style={{ color: flow.color }} />
                    <h3 className="text-sm font-semibold text-gray-700">{flow.title}</h3>
                    <span className="text-[10px] text-gray-400">({screens.length})</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {screens.map(screen => (
                      <div key={screen.id} className="rounded-lg border overflow-hidden bg-white hover:shadow-sm transition-shadow cursor-pointer"
                        onClick={() => screen.screenshot_url && setLightboxImg(`${API}${screen.screenshot_url}`)}
                        data-testid={`grid-screen-${screen.id}`}>
                        {screen.screenshot_url ? (
                          <img src={`${API}${screen.screenshot_url}`} alt={screen.title} className="w-full aspect-[9/16] object-contain bg-white rounded-lg border border-gray-200" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
                            <Monitor className="w-4 h-4 text-gray-300" />
                          </div>
                        )}
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium text-gray-700 truncate">{screen.title}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxImg(null)} data-testid="lightbox">
          <img src={lightboxImg} alt="Screenshot" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
